/* eslint-disable complexity */
import ApiCallManager from '../models/apiCallManager';
import Campaign from '../models/campaign';

/**
 * @ngdoc service
 * @name campaignsService
 * @description
 * This service is meant to fetch all the campaigns.
 *
 * @memberof common
 */
class CampaignsService extends ApiCallManager {
  /**
   * Class constructor.
   * @param {Object}  appConstant            The app configuration dictionary.
   * @param {Service} appUtils               The service to get the page query param.
   * @param {Service} contentCreatorAdminAPI The service to fetch the customer campaigns.
   * @param {Service} fileUtils              To upload the image to the BE.
   * @ignore
   */
  constructor(
    appConstant,
    appUtils,
    contentCreatorAdminAPI,
    fileUtils,
  ) {
    'ngInject';

    super(
      appUtils,
      (content) => content.map((campaign) => new Campaign(
        campaign,
        appConstant.settings.campaigns,
      )),
      contentCreatorAdminAPI.getCampaigns.bind(contentCreatorAdminAPI),
    );

    /**
     * The local reference to the `contentCreatorAdminAPI` service.
     * @type {Object}
     */
    this.contentCreatorAdminAPI = contentCreatorAdminAPI;
    /**
     * The local reference to the `fileUtils` service.
     * @type {Object}
     */
    this.fileUtils = fileUtils;
    /**
     * The local reference to the `errors` object.
     * @type {Object}
     */
    this.errors = appConstant.errors.campaigns;
    /**
     * The local reference to the `maxChars` property.
     * @type {Object}
     */
    this.maxChars = appConstant.maxChars.campaigns;
    /**
     * The local reference to the `campaignsSettings` property.
     * @type {Object}
     */
    this.campaignsSettings = appConstant.settings.campaigns;
  }
  /**
   * Transforms a campaign object into a new object compliant with the API so it can be saved.
   * @param {Object} campaign the campaign to process.
   * @return {Object} the campaign ready for save.
   * @property {Number}  id                the campaign id.
   * @property {String}  name              the campaign name.
   * @property {String}  cover_image       the campaign cover image.
   * @property {String}  short_description the campaign short description.
   * @property {String}  long_description  the campaign long description.
   * @property {Array}   guidelines        the campaign guidelines.
   * @property {Object}  settings          the campaign settings.
   */
  _processForSave(campaign) {
    const guidelines = [];

    campaign.guidelines.positive.forEach((item) => {
      if (item.value) {
        guidelines.push({
          connotation: 'positive',
          head: item.value,
        });
      }
    });
    campaign.guidelines.negative.forEach((item) => {
      if (item.value) {
        guidelines.push({
          connotation: 'negative',
          head: item.value,
        });
      }
    });

    const settings = {};
    if (campaign.skipModeration) {
      settings[this.campaignsSettings.skipModeration.key]
                = this.campaignsSettings.skipModeration.val;
    }

    return {
      id: campaign.id,
      name: campaign.name,
      cover_image: campaign.coverImage,
      short_description: campaign.shortDescription,
      long_description: campaign.longDescription,
      guidelines,
      settings,
    };
  }
  /**
   * Delete a campaign in the BE.
   * @param {Object} campaign the campaign to delete.
   * @return {Promise}
   */
  deleteCampaign(campaign) {
    return this.contentCreatorAdminAPI.deleteCampaign(campaign)
    .then(() => {
      this.removeContent(campaign);
    });
  }
  /**
   * Check if we should display an error for the specified campaign property.
   * @param {String}  campaign          the campaign to validate.
   * @param {String}  property          the property of the campaign to validate.
   * @param {Boolean} validateMaxLength should we validate max length.
   */
  hasErrorCampaign(campaign, property, validateMaxLength) {
    const field = campaign[property];
    const error = campaign.errors[property] && !field;
    return validateMaxLength
      ? error || field.length > this.maxChars[property]
      : error;
  }
  /**
   * Check if we should display an error for the specified guideline.
   * @param {Object}  campaign          the campaign to validate the guidelines.
   * @param {String}  property          the property of the guideline to validate.
   * @param {Number}  index             the guideline index to validate.
   * @param {Boolean} validateMaxLength should we validate max length.
   */
  hasErrorGuideline(campaign, property, index, validateMaxLength) {
    const field = campaign.guidelines[property][index].value;
    const error = index === 0 && campaign.errors.guidelines[property] && !field;
    return validateMaxLength
      ? error || field.length > this.maxChars.guidelines[property]
      : error;
  }
  /**
   * Get the Campaigns list from the BE.
   * @param {Boolean} refresh flag to force a refresh of the Campaigns.
   * @return {Promise}
   */
  getCampaigns(refresh) {
    return this._getContent(refresh);
  }
  /**
   * Get a Campaign by its Id.
   * @return {Promise}
   */
  getCampaignById(campaignId) {
    return this._getContentById(campaignId);
  }
  /**
   * Get a default campaign for edition.
   * @return {Object} the default campaign ready for edit.
   */
  getDefaultCampaignForEdit() {
    return Campaign.getDefaultForEdit();
  }
  /**
   * Save a campaign in the BE.
   * @param {Object} campaign campaign to save.
   * @return {Promise}
   */
  saveCampaign(campaign) {
    const processedCampaign = this._processForSave(campaign);

    return campaign.id ?
      this.contentCreatorAdminAPI.editCampaign(processedCampaign)
      .then((editedCampaign) => this.updateContent(
        new Campaign(editedCampaign, this.campaignsSettings),
        (oldCampaign) => {
          // Delete the campaign arrays
          delete oldCampaign.guidelines;
          delete oldCampaign.settings;
        },
      )) :
      this.contentCreatorAdminAPI.createCampaign(processedCampaign)
      .then((newCampaign) => this.addContent(new Campaign(newCampaign, this.campaignsSettings)));
  }
  /**
   * Upload the cover image of a campaign.
   * @param {File} file the image file to upload.
   * @return {Promise}
   */
  uploadCoverImage(file) {
    return this.fileUtils.uploadImage(file, 'campaign_cover_images')
    .then((result) => ({
      coverImage: result.resource_url,
      errors: {
        coverImageExtra: '',
      },
    }))
    .catch((error) => ({
      errors: {
        coverImageExtra: error.message,
      },
    }));
  }
  /**
   * Validate if a campaign is valid to be saved.
   * @param {Object} campaign campaign to validate.
   * @return {Boolean} if the campaign passed the validation.
   */
  validateCampaign(campaign) {
    // Validate required fields
    campaign.errors.name = !campaign.name;
    campaign.errors.coverImage = !campaign.coverImage;
    campaign.errors.guidelines.positive = !campaign.guidelines.positive[0].value;
    campaign.errors.guidelines.negative = !campaign.guidelines.negative[0].value;

    // Validate that the name is unique only
    campaign.errors.nameExtra = this._existContentWithProperty(campaign, 'name', true) ?
      this.errors.duplicateName :
      '';

    // If there are errors return false
    if (
      campaign.errors.name ||
      campaign.errors.nameExtra ||
      campaign.errors.coverImage ||
      campaign.errors.coverImageExtra ||
      campaign.errors.guidelines.positive ||
      campaign.errors.guidelines.negative
    ) {
      return false;
    }

    // Validate max lengths
    return this.maxChars.name >= campaign.name.length &&
      this.maxChars.shortDescription >= campaign.shortDescription.length &&
      this.maxChars.longDescription >= campaign.longDescription.length &&
      !campaign.guidelines.positive
      .some((guideline) => guideline.value.length > this.maxChars.guidelines.positive) &&
      !campaign.guidelines.negative
      .some((guideline) => guideline.value.length > this.maxChars.guidelines.negative);
  }
}

export default CampaignsService;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/services/campaigns.js